/**
 * Re-pricing the page as the customer taps — `docs/proposal.md` §4.4, §12.1.
 *
 * The arithmetic the browser does is `selectionTotals()` and `monthSplit()`,
 * both pure and both instant. What it *cannot* work out is the payment
 * schedule: the invoices come out of the billing plans, which the page never
 * receives. So the selection goes to `POST /portal/quotes/:id/price` and the
 * rows come back.
 *
 * **Debounced, and the last answer wins.** A toggle is a request, and
 * somebody comparing Silver with Platinum will fire four in two seconds; 150ms
 * of quiet means one. Each run carries a token and a late answer to a
 * superseded question is dropped rather than drawn — out-of-order responses
 * are the one way this could put another selection's schedule on the screen.
 *
 * **It never blocks and never throws at the page.** A failed price call leaves
 * the previous rows in place with `priced` false, which is the sentence "we
 * will confirm the payment schedule" rather than a wrong one — the same answer
 * the page gives before the first response arrives.
 */
import { useEffect, useRef, useState } from 'react'
import type {
  InvoiceTerms, MonthSplit, ProposalPayment, ProposalSelection,
} from '@rowted/shared'

export interface PriceAnswer {
  payments: ProposalPayment[]
  /** The server's own `monthSplit`. The page draws its own; this is the check. */
  month?: MonthSplit | null
  /** True when the offer moved under the customer (§4.5). */
  stale?: boolean
  /** When the chosen plans' invoices are due (§9.4). */
  invoiceTerms?: InvoiceTerms | null
}

export type PriceCall = (selection: ProposalSelection) => Promise<PriceAnswer>

export interface Priced {
  payments: ProposalPayment[]
  /** Do the rows on hand describe the selection on screen? */
  priced: boolean
  /** The offer changed while they were reading it. */
  stale: boolean
  /** When those payments are due — the sentence under them (§9.4). */
  invoiceTerms: InvoiceTerms | null
}

/**
 * `opening` is what the server already priced for `view.initial`, so the page
 * has a schedule to draw on the first paint without a round trip.
 */
export function usePrice(
  selection: ProposalSelection,
  opening: ProposalPayment[],
  initial: ProposalSelection,
  onPrice?: PriceCall,
  /** `view.invoiceTerms` — the terms for the opening selection. */
  openingTerms: InvoiceTerms | null = null,
): Priced {
  const [state, setState] = useState<Priced>(() => ({
    payments: opening, priced: sameSelection(selection, initial), stale: false,
    invoiceTerms: openingTerms,
  }))

  /* Which request is current. A stale answer is dropped, never drawn. */
  const token = useRef(0)
  const key = selectionKey(selection)

  useEffect(() => {
    /*
     * No price call — the office preview and the builder's tab, which have no
     * portal session. The opening rows stand while the selection is the one
     * they were priced for, and the sentence takes over once it moves.
     */
    if (!onPrice) {
      setState({
        payments: opening, priced: sameSelection(selection, initial), stale: false,
        invoiceTerms: openingTerms,
      })
      return
    }

    const mine = token.current + 1
    token.current = mine
    /* Mark what is on screen as no longer describing the selection, at once. */
    setState((was) => ({ ...was, priced: sameSelection(selection, initial) }))

    const timer = setTimeout(() => {
      void onPrice(selection)
        .then((answer) => {
          if (token.current !== mine) return
          setState({
            payments: answer.payments,
            priced: true,
            stale: answer.stale ?? false,
            invoiceTerms: answer.invoiceTerms ?? null,
          })
        })
        .catch(() => {
          /* Reported by not claiming a schedule, which the page already says. */
          if (token.current === mine) setState((was) => ({ ...was, priced: false }))
        })
    }, 150)

    return () => clearTimeout(timer)
    /*
     * Keyed on the selection's **content**, not its identity: the page hands
     * a new object down on every render, and an effect watching the object
     * would re-request for a selection that had not changed. `selection`,
     * `opening` and `initial` are read inside and are all facts about the
     * same key or about the view, which does not change while the page is
     * open.
     */
  }, [key, onPrice])

  return state
}

/** A selection as one string, for an effect to compare rather than an object. */
function selectionKey(sel: ProposalSelection): string {
  const choose = Object.keys(sel.choose).sort()
    .map((group) => `${group}=${sel.choose[group] ?? 'none'}`).join(',')
  return [choose, [...sel.visits].sort().join(','), [...sel.services].sort().join(','),
    [...sel.lines].sort().join(',')].join('|')
}

/**
 * Is this the selection the server priced?
 *
 * Order does not matter and `choose` is a map, so this compares what each part
 * *holds* rather than how it is written.
 */
export function sameSelection(a: ProposalSelection, b: ProposalSelection): boolean {
  return selectionKey(a) === selectionKey(b)
}
